"use client";

import React, { useMemo, useState, useEffect } from "react";

type Category = {
  id: string;
  name: string;
  image?: string;
};

type GiftLookBookProps = {
  categories?: Category[];
};

export default function GiftLookBookSection({ categories = [] }: GiftLookBookProps) {


  const displayCategories = useMemo<Category[]>(() => {
    if (categories.length > 0) {
      const shuffled = [...categories].sort(() => 0.5 - Math.random());
      return shuffled.slice(0, 5);
    }

    return [
      {
        id: "1",
        name: "Birthday Surprises 🎂",
        image: "https://images.unsplash.com/photo-1513151233558-d860c5398176?q=80&w=1200",
      },
      {
        id: "2",
        name: "Wedding & Anniversary 💍",
        image: "https://images.unsplash.com/photo-1464349095431-e9a21285b5f3?q=80&w=1200",
      },
      {
        id: "3",
        name: "Baby & Kids Gifts 🍼",
        image: "https://images.unsplash.com/photo-1515488042361-ee00e0ddd4e4?q=80&w=1200",
      },
      {
        id: "4",
        name: "Luxury Collection 💎",
        image: "https://img.freepik.com/premium-photo/luxury-gold-diamond-jewelry-shop-display_1015182-44628.jpg",
      },
      {
        id: "5",
        name: "Personalized Treasures ✨",
        image: "https://images.unsplash.com/photo-1513885535751-8b9238bd345a?q=80&w=1200",
      },
    ];
  }, [categories]);
  // generate sparkles/confetti config
  const [sparkles, setSparkles] = useState<
    { left: number; size: number; duration: number; delay: number; opacity: number; emoji: string }[]
  >([]);

  useEffect(() => {
    const count = 40;
    const emojis = ["🎁", "✨", "🎀", "🎉", "💝", "⭐"];
    const arr = Array.from({ length: count }).map(() => ({
      left: Math.random() * 100,
      size: 12 + Math.random() * 8,
      duration: 5 + Math.random() * 7,
      delay: Math.random() * 5,
      opacity: 0.3 + Math.random() * 0.5,
      emoji: emojis[Math.floor(Math.random() * emojis.length)],
    }));
    setSparkles(arr);
  }, []);

  const sectionStyle: React.CSSProperties = {
    padding: "80px 20px",
    background: "linear-gradient(135deg, #fffbfbff 0%, #f7f7f7ff 50%, #f8e1eeff 100%)",
    color: "#2C2C2C",
    textAlign: "center",
    position: "relative",
    overflow: "hidden",
  };

  // Decorative backgrounds
  const bgCircle1: React.CSSProperties = {
    position: "absolute",
    top: "-80px",
    right: "-120px",
    width: "350px",
    height: "350px",
    background: "radial-gradient(circle, rgba(255,182,193,0.15) 0%, transparent 70%)",
    borderRadius: "50%",
    pointerEvents: "none",
    zIndex: 0,
  };

  const bgCircle2: React.CSSProperties = {
    position: "absolute",
    bottom: "-60px",
    left: "-100px",
    width: "300px",
    height: "300px",
    background: "radial-gradient(circle, rgba(255,215,180,0.12) 0%, transparent 70%)",
    borderRadius: "50%",
    pointerEvents: "none",
    zIndex: 0,
  };

  const badgeStyle: React.CSSProperties = {
    display: "inline-block",
    padding: "8px 20px",
    background: "linear-gradient(135deg, #FF6B6B 0%, #FF8E53 100%)",
    color: "white",
    borderRadius: "50px",
    fontSize: "13px",
    fontWeight: 800,
    letterSpacing: "1px",
    textTransform: "uppercase",
    marginBottom: "20px",
    boxShadow: "0 4px 16px rgba(255,107,107,0.3)",
  };

  const titleStyle: React.CSSProperties = {
    fontSize: "48px",
    fontWeight: 900,
    marginBottom: "16px",
    letterSpacing: "-1px",
    background: "linear-gradient(135deg, #FF6B6B 0%, #FF8E53 100%)",
    WebkitBackgroundClip: "text",
    WebkitTextFillColor: "transparent",
    backgroundClip: "text",
  };

  const subtitleStyle: React.CSSProperties = {
    fontSize: "18px",
    maxWidth: "700px",
    margin: "0 auto 50px auto",
    color: "#666",
    lineHeight: 1.7,
    fontWeight: 500,
  };

  const gridStyle: React.CSSProperties = {
    display: "grid",
    gap: "28px",
    gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))",
    maxWidth: "1400px",
    margin: "0 auto",
    position: "relative",
    zIndex: 1,
  };

  const cardStyle: React.CSSProperties = {
    position: "relative",
    borderRadius: "28px",
    overflow: "hidden",
    cursor: "pointer",
    transition: "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
    willChange: "transform",
    background: "#FFFFFF",
    border: "2px solid #FFF0E6",
    boxShadow: `
      0 2px 4px rgba(255, 255, 255, 0.08),
      0 4px 8px rgba(255,140,83,0.1),
      0 8px 16px rgba(255,140,83,0.12),
      0 16px 32px rgba(63, 63, 63, 0.08)
    `,
  };

  const imageWrapperStyle: React.CSSProperties = {
    position: "relative",
    width: "100%",
    height: "380px",
    overflow: "hidden",
    background: "linear-gradient(135deg, #FFF5F0 0%, #FFE9E0 100%)",
  };

  const imageStyle: React.CSSProperties = {
    width: "100%",
    height: "100%",
    objectFit: "cover",
    transition: "transform 0.6s cubic-bezier(0.4, 0, 0.2, 1), filter 0.3s ease",
    display: "block",
  };

  const imageShineStyle: React.CSSProperties = {
    position: "absolute",
    inset: 0,
    left: "-120%",
    width: "45%",
    height: "100%",
    background: "linear-gradient(120deg, transparent, rgba(255,215,180,0.5), transparent)",
    transform: "skewX(-18deg)",
    pointerEvents: "none",
    zIndex: 2,
  };

  const overlayStyle: React.CSSProperties = {
    position: "absolute",
    bottom: 0,
    left: 0,
    right: 0,
    padding: "24px 20px",
    background: "linear-gradient(to top, rgba(0,0,0,0.85) 0%, rgba(0,0,0,0.6) 50%, transparent 100%)",
    color: "#fff",
    zIndex: 3,
  };

  const categoryTitleStyle: React.CSSProperties = {
    fontSize: "22px",
    fontWeight: 900,
    letterSpacing: "0.3px",
    marginBottom: "8px",
    textShadow: "0 2px 12px rgba(0,0,0,0.4)",
  };

  const categoryDescStyle: React.CSSProperties = {
    fontSize: "14px",
    fontWeight: 600,
    opacity: 0.95,
    letterSpacing: "0.3px",
  };

  const sparklesContainerStyle: React.CSSProperties = {
    position: "absolute",
    inset: 0,
    pointerEvents: "none",
    zIndex: 2,
    overflow: "hidden",
  };

  const keyframes = `
    @keyframes sparkleFloat {
      0% { transform: translateY(-5vh) rotate(0deg); opacity: 0; }
      10% { opacity: 0.8; }
      50% { transform: translateY(50vh) rotate(180deg); opacity: 0.9; }
      90% { opacity: 0.6; }
      100% { transform: translateY(110vh) rotate(360deg); opacity: 0; }
    }

    @keyframes shineImage {
      to { left: 150%; }
    }
    
    @media (max-width: 640px) {
      .gift-image-wrapper {
        height: 260px !important;
      }
    }
  `;

  if (displayCategories.length === 0) return null;

  console.log("Display Categories:", displayCategories); // Debug log

  return (
    <>
      <style>{keyframes}</style>

      <section style={sectionStyle}>
        {/* Background decorations */}
        <div style={bgCircle1} />
        <div style={bgCircle2} />

        <div style={badgeStyle}>Gift Ideas & Inspiration</div>
        
        <h2 style={titleStyle}>Gift Gallery – Find Your Perfect Gift</h2>
        
        <p style={subtitleStyle}>
          Explore our curated gift collections for every occasion and every special person in your life. From heartfelt surprises to luxury treasures.
        </p>

        {/* Floating sparkles/emojis */}
        <div style={sparklesContainerStyle}>
          {sparkles.map((s, i) => {
            const sparkleStyle: React.CSSProperties = {
              position: "absolute",
              top: "-5vh",
              left: `${s.left}%`,
              fontSize: `${s.size}px`,
              opacity: s.opacity,
              transform: "translateY(0)",
              animation: `sparkleFloat ${s.duration}s linear infinite`,
              animationDelay: `${s.delay}s`,
            };
            return (
              <div key={i} style={sparkleStyle}>
                {s.emoji}
              </div>
            );
          })}
        </div>

        <div style={gridStyle}>
          {displayCategories.map((category, i) => (
            <div
              key={category.id}
              style={cardStyle}
              onMouseEnter={(e) => {
                const el = e.currentTarget;
                el.style.transform = "translateY(-12px) scale(1.02)";
                el.style.boxShadow = `
                  0 4px 8px rgba(255,107,107,0.12),
                  0 8px 16px rgba(255,140,83,0.15),
                  0 16px 32px rgba(255,107,107,0.18),
                  0 32px 64px rgba(255,107,107,0.12)
                `;
                const img = el.querySelector(".gift-image") as HTMLImageElement | null;
                if (img) {
                  img.style.transform = "scale(1.15)";
                  img.style.filter = "brightness(1.08)";
                }
                const shine = el.querySelector(".image-shine") as HTMLElement | null;
                if (shine) {
                  shine.style.animation = "shineImage 0.8s ease forwards";
                }
              }}
              onMouseLeave={(e) => {
                const el = e.currentTarget;
                el.style.transform = "translateY(0) scale(1)";
                el.style.boxShadow = `
                  0 2px 4px rgba(255, 255, 255, 0.08),
                  0 4px 8px rgba(255,140,83,0.1),
                  0 8px 16px rgba(255,140,83,0.12),
                  0 16px 32px rgba(63, 63, 63, 0.08)
                `;
                const img = el.querySelector(".gift-image") as HTMLImageElement | null;
                if (img) {
                  img.style.transform = "scale(1)";
                  img.style.filter = "brightness(1)";
                }
                const shine = el.querySelector(".image-shine") as HTMLElement | null;
                if (shine) {
                  shine.style.animation = "none";
                  shine.style.left = "-120%";
                }
              }}
            >
              <div className="gift-image-wrapper" style={imageWrapperStyle}>
                <img 
                  className="gift-image" 
                  src={category.image || "/noimage.png"} 
                  alt={category.name} 
                  style={imageStyle} 
                />
                <span className="image-shine" style={imageShineStyle} />
              </div>
              
              <div style={overlayStyle}>
                <div style={categoryTitleStyle}>{category.name}</div>
                <div style={categoryDescStyle}>Explore Collection →</div>
              </div>
            </div>
          ))}
        </div>
      </section>
    </>
  );
}